// Copyright (c) Bentley Systems
import {
  ActionColumn,
  Button,
  ButtonGroup,
  DefaultCell,
  DropdownMenu,
  Flex,
  IconButton,
  InformationPanel,
  InformationPanelBody,
  InformationPanelContent,
  InformationPanelHeader,
  InformationPanelWrapper,
  MenuItem,
  Table,
  Text,
  Tooltip,
} from "@itwin/itwinui-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { selectAccessToken } from "../app/authorization-slice";
import {
  customCellStringSort,
  FormatDate,
  setErrorState,
} from "../Helpers/helper-functions";
import {
  selectMainMenuHeightWithMargin,
  selectSideMenuWidthWithMargin,
} from "../app/header-slice";
import { selectConfirmation, setConfirmation } from "../app/confirmation-slice";
import { setformOpen } from "../app/form-open-slice";
import {
  CellProps,
  CellRendererProps,
  Column,
  Row,
} from "@itwin/itwinui-react/react-table";
import IconByName from "../Icons/IconByName";
import { SvgMore } from "@itwin/itwinui-icons-react";
import { ConfigManager } from "../Helpers/config-manager";
import { CatalogGeneralInfo } from "./Models/catalog-general-info";
import { mapDataToModels } from "../Helpers/map-response";
import ProjectForm from "./ProjectForm";
import { DawComponent } from "../Helpers/daw-component";
import { useNodePermission } from "../Helpers/useNodePermission";
import { useODataTableState } from "../Helpers/useODataTableState";
import { useElementHeights } from "../Helpers/useElementHeights";
import { ODataTextFilter } from "../Helpers/ODataTextFilter";
import { useDebouncedFetch } from "../Helpers/useDebouncedFetch";
import {
  PROJECT_LIST_VIEWS,
  ProjectListView,
  ProjectListViewDescriptions,
  ProjectListViewLabels,
} from "./Models/project-list-view";

type ProjectTableRow = {
  name: string;
  uniqueName: string;
  version: string;
  projectStageId: string;
  lastChange: Date | undefined;
  lastChangeByName: string;
  isOdata: boolean;
  project: CatalogGeneralInfo;
};

const ProjectManagement = () => {
  const dispatch = useAppDispatch();
  const sideMenuWidth = useAppSelector(selectSideMenuWidthWithMargin);
  const mainMenuHeight = useAppSelector(selectMainMenuHeightWithMargin);
  const headerDivRef = useRef<HTMLDivElement>(null);
  const headerButtonsDivRef = useRef<HTMLDivElement>(null);
  const [headerDivHeight, headerCbDivHeight] = useElementHeights(
    headerDivRef,
    headerButtonsDivRef,
  );
  const { isNodeAllowed } = useNodePermission(
    DawComponent.DAW_COMPONENT_GENERAL_PROJECTS,
  );
  const [projects, setProjects] = useState<CatalogGeneralInfo[]>([]);
  const [selectedProject, setSelectedProject] = useState<CatalogGeneralInfo>();
  const accessToken = useAppSelector(selectAccessToken);
  const [fRollUrl, setFrollUrl] = useState<string>();
  const [infoPanelHeader, setInfoPanelHeader] = useState<string>();
  const confirmationState = useAppSelector(selectConfirmation);
  const [actionCount, setActionCount] = useState<number>(0);
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const [view, setView] = useState<ProjectListView>("All");

  const allState = useODataTableState();
  const catalogState = useODataTableState();
  const previewState = useODataTableState();
  const integrationTestsState = useODataTableState();

  const tableStates: Record<ProjectListView, ReturnType<typeof useODataTableState>> = {
    All: allState,
    Catalog: catalogState,
    Preview: previewState,
    IntegrationTests: integrationTestsState,
  };
  const {
    pageSize,
    sortBy,
    isLoading,
    tableFiltersState,
    setTotalCount,
    setIsLoading,
    handleSort,
    handleFilter,
    paginator,
    buildOrderBy,
    buildFilterStr,
    skip,
    top,
  } = tableStates[view];

  // Switching tabs is a discrete click, not a keystroke, so update visible
  // state immediately instead of leaving stale rows on screen for the
  // 300ms fetch debounce window.
  const onViewChange = (v: ProjectListView) => {
    if (v === view) { return; }
    setProjects([]);
    tableStates[v].setIsLoading(true);
    setView(v);
  };

  function onFrollClose(refreshList: boolean) {
    setFrollUrl(undefined);
    setSelectedProject(undefined);
    setInfoPanelHeader(undefined);
    setIsDuplicate(false);
    if (refreshList) {
      refreshListFunction();
    }
  }

  function refreshListFunction() {
    setActionCount((prevCount) => prevCount + 1);
  }

  useEffect(() => {
    if (fRollUrl === undefined) {
      dispatch(setformOpen({ isOpen: false }));
    } else {
      dispatch(setformOpen({ isOpen: true }));
    }
  }, [dispatch, fRollUrl]);

  const deleteProject = useCallback(
    async (id: string): Promise<boolean> => {
      setIsLoading(true);
      let result = true;
      const deleteUrl =
        `${ConfigManager.projectProviderUrl}/api/Projects/${id}`;
      const runIdDel = crypto.randomUUID();
      await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runIdDel,
        },
      })
        .then(function (response) {
          if (!response.ok) {
            setErrorState(response.status, dispatch, runIdDel, true);
            result = false;
          }
        })
        .finally(() => setIsLoading(false));

      return result;
    },
    [accessToken, dispatch, setIsLoading],
  );

  const onDeleteProjectClick = useCallback(
    async (proj: CatalogGeneralInfo) => {
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: async () => {
            const deleted = await deleteProject(proj.Id);
            if (deleted) {
              refreshListFunction();
            }
          },
          caption: "Delete confirmation",
          text: "Are you sure you want to delete this project?",
          okText: "Yes",
          cancelText: "No",
        }),
      );
    },
    [deleteProject, dispatch, refreshListFunction],
  );

  const buttonCellTemplate = useCallback(
    (cellProps: CellProps<any>) => {
      const menuItems = (close: () => void) => {
        const menuItemStyle = { height: "1rem", width: "1rem" };

        const items: React.JSX.Element[] = [
          <MenuItem
            key="miDuplicate"
            startIcon={<IconByName name={"duplicate"} style={menuItemStyle} />}
            onClick={() => {
              close();
              setSelectedProject(cellProps.row.original.project);
              setIsDuplicate(true);
              setFrollUrl("project");
            }}
          >
            Duplicate
          </MenuItem>,
          <MenuItem
            key="miEdit"
            startIcon={<IconByName name={"edit"} style={menuItemStyle} />}
            onClick={() => {
              close();
              setSelectedProject(cellProps.row.original.project);
              setIsDuplicate(false);
              setFrollUrl("project");
            }}
          >
            Edit
          </MenuItem>,
          <MenuItem
            key="miDelete"
            startIcon={<IconByName name={"delete"} style={menuItemStyle} />}
            onClick={() => {
              close();
              void onDeleteProjectClick(cellProps.row.original.project);
            }}
          >
            Delete
          </MenuItem>,
        ];

        return items;
      };

      return (
        <>
          <DropdownMenu menuItems={menuItems}>
            <IconButton
              styleType="borderless"
              onClick={(e) => e.stopPropagation()}
              label="Actions"
            >
              <SvgMore />
            </IconButton>
          </DropdownMenu>
          <div className="becs-table-cell-shadow-left-elist" />
        </>
      );
    },
    [onDeleteProjectClick, setFrollUrl, setSelectedProject],
  );

  const columns = useMemo<Column<ProjectTableRow>[]>(() => {
    return [
      {
        Header: "Name",
        accessor: "name",
        id: "name",
        minWidth: 350,
        Filter: ODataTextFilter,
      },
      {
        Header: "Unique name",
        accessor: "uniqueName",
        id: "uniqueName",
        minWidth: 350,
        Filter: ODataTextFilter,
      },
      {
        Header: "Version",
        accessor: "version",
        id: "version",
        minWidth: 140,
        Filter: ODataTextFilter,
      },
      {
        Header: "Odata",
        accessor: "isOdata",
        sortType: customCellStringSort,
        id: "isOdata",
        width: 100,
        disableToggleVisibility: false,
        cellRenderer: (props: CellRendererProps<ProjectTableRow>) => {
          return (
            <DefaultCell
              {...props}
              style={{ alignItems: "center", justifyContent: "center" }}
            >
              {props.cellProps.value === true ? (
                <IconByName name="StatusSuccessColor" />
              ) : (
                <IconByName name="StatusSuccessColorMuted" />
              )}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Last change",
        accessor: "lastChange",
        id: "lastChange",
        minWidth: 200,
        //TODO - update service?
        //sortType: customCellStringSort,
        // Filter: tableFilters.DateRangeFilter({
        //   formatDate(date) {
        //     return date.toLocaleString();
        //   },
        // }),
        // filter: "betweenDate",
        cellRenderer: (props: CellRendererProps<ProjectTableRow>) => {
          return (
            <DefaultCell {...props}>
              {FormatDate(props.cellProps.value)}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Last change by",
        accessor: "lastChangeByName",
        id: "lastChangeByName",
        minWidth: 200,
        Filter: ODataTextFilter,
      },
      {
        Header: "Stage",
        accessor: "projectStageId",
        id: "projectStageId",
        minWidth: 120,
        // TODO - update service?
        //Filter: tableFilters.TextFilter(),
      },
      {
        ...ActionColumn({ columnManager: true }),
        Cell: buttonCellTemplate,
        sticky: "right",
      },
    ];
  }, [buttonCellTemplate]);

  const projectsData = useMemo<ProjectTableRow[]>(() => {
    return projects.map((project) => ({
      name: project.Name,
      uniqueName: project.UniqueName,
      version: project.Version,
      projectStageId: project.ProjectStageId,
      lastChange: project.LastChange,
      lastChangeByName: project.LastChangeByName,
      isOdata: project.IsOdata,
      project,
    }));
  }, [projects]);

  function getZindex(): number {
    return confirmationState.isConfirmationOpen ? 0 : 2;
  }

  const fetchProjects = useCallback(
    async (signal?: AbortSignal) => {
      if (!isNodeAllowed) {
        setIsLoading(false);
        return;
      }

      setProjects([]);
      const orderby = buildOrderBy();
      const filterStr = buildFilterStr();
      // Server defaults to All when the param is absent
      const viewParam = view === "All" ? "" : `&view=${view}`;

      const url =
        `${ConfigManager.projectProviderUrl}/odata/Projects?count=true` +
        `&$select=Id,Name,UniqueName,Version,IsOdata,LastChange,LastChangeByName,ProjectStageId` +
        `&$skip=${skip}&$top=${top}${orderby}${filterStr}${viewParam}`;
      const runId = crypto.randomUUID();
      setIsLoading(true);
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal,
        });
        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        }
        const data = await response.json();
        if (data && data.value) {
          const projectsDtos: CatalogGeneralInfo[] =
            mapDataToModels<CatalogGeneralInfo>(data.value);
          projectsDtos.forEach((p) => {
            if (p.LastChange !== undefined) {
              p.LastChange = new Date(p.LastChange);
            }
          });
          setProjects(projectsDtos);
          setTotalCount(data["@odata.count"] || 0);
        }
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      isNodeAllowed,
      accessToken,
      dispatch,
      skip,
      top,
      view,
      buildOrderBy,
      buildFilterStr,
      setIsLoading,
      setTotalCount,
    ],
  );

  // Keep a ref to fetchProjects so the actionCount effect can call it
  // without re-triggering when fetchProjects identity changes.
  const fetchProjectsRef = useRef(fetchProjects);
  useEffect(() => {
    fetchProjectsRef.current = fetchProjects;
  }, [fetchProjects]);

  // Debounced fetch — covers search, pagination, sort, filter changes
  useDebouncedFetch(fetchProjects, 300);

  // Re-fetch immediately when actionCount changes (e.g., after create/delete)
  useEffect(() => {
    if (actionCount === 0) { return; } // skip initial mount
    const abortController = new AbortController();
    void fetchProjectsRef.current(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [actionCount]);

  function navigateToInfPanelFunction(navFrolUrl: string) {
    setFrollUrl(navFrolUrl);
  }

  const onSetInfoPanelHeader = useCallback((header: string) => {
    setInfoPanelHeader(header);
  }, []);

  const allHeadersHeight = mainMenuHeight + headerDivHeight + headerCbDivHeight;

  const expandedSubComponent = useCallback((row: Row<ProjectTableRow>) => {
    return (
      <div style={{ width: "100%" }}>
        <ProjectForm isEditable={false} isDuplicate={false} project={row.original.project} />
      </div>
    );
  }, []);

  return (
    <div style={{ height: "100%" }}>
      <InformationPanelWrapper
        style={{ height: "100%", maxWidth: "100%", borderRadius: 0 }}
      >
        <div>
          {fRollUrl !== undefined && <div className="overlay" />}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={
                fRollUrl !== undefined
                  ? { pointerEvents: "none", opacity: "0.8" }
                  : {}
              }
            >
              <div className="becs-infopanelbody-footer-line">
                <div className="becs-all-margin" ref={headerDivRef}>
                  <div className="rowWrap">
                    <Text variant="title">{"Projects management"}</Text>
                  </div>
                </div>
              </div>

              <div className="becs-ltr-margin">
                <div>
                  <Flex className="becs-tb-margin" ref={headerButtonsDivRef}>
                    <Button
                      key="newProjectBtn"
                      as="button"
                      onClick={() => {
                        setSelectedProject(undefined);
                        setIsDuplicate(false);
                        setFrollUrl("projects");
                      }}
                      styleType="high-visibility"
                    >
                      <div>New project</div>
                    </Button>

                    <Flex.Spacer />

                    <ButtonGroup
                      role="toolbar"
                      aria-label="Filter projects by view"
                      className="becs-project-tablist"
                    >
                      {PROJECT_LIST_VIEWS.map((v) => (
                        <Tooltip key={v} content={ProjectListViewDescriptions[v]} placement="top">
                          <Button
                            styleType={view === v ? "high-visibility" : "default"}
                            aria-pressed={view === v}
                            onClick={() => onViewChange(v)}
                            className="becs-project-tab"
                          >
                            {ProjectListViewLabels[v]}
                          </Button>
                        </Tooltip>
                      ))}
                    </ButtonGroup>
                  </Flex>
                </div>
                <div style={{ maxWidth: `calc(100vw - ${sideMenuWidth}px)` }}>
                  <Table
                    key={view}
                    columns={columns}
                    emptyTableContent="No data."
                    data={projectsData}
                    isLoading={isLoading}
                    style={{
                      height: `calc(100vh - ${allHeadersHeight}px)`,
                      width: `calc(100vw - ${sideMenuWidth}px)`,
                    }}
                    isSortable={true}
                    isResizable={false}
                    density="extra-condensed"
                    styleType="zebra-rows"
                    manualPagination
                    manualSortBy
                    manualFilters
                    paginatorRenderer={paginator}
                    pageSize={pageSize}
                    initialState={{
                      sortBy,
                      filters: tableFiltersState,
                    }}
                    isSelectable={true}
                    selectionMode="single"
                    subComponent={expandedSubComponent}
                    onSort={handleSort}
                    onFilter={handleFilter}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {fRollUrl !== undefined && (
          <InformationPanel
            resizable
            isOpen={fRollUrl !== undefined}
            style={{
              width: "40%",
              maxWidth: "70%",
              minWidth: "10%",
              zIndex: getZindex(),
            }}
            className="iui-infoPanel"
          >
            <InformationPanelHeader style={{ padding: 0 }}>
              <div className="becs-all-margin">
                <Text
                  variant="title"
                  style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                >
                  {infoPanelHeader}
                </Text>
              </div>
            </InformationPanelHeader>
            <InformationPanelBody style={{ padding: 0 }}>
              <InformationPanelContent
                displayStyle="default"
                style={{ padding: 0, height: "100%" }}
              >
                <ProjectForm
                  project={selectedProject}
                  isEditable={true}
                  isDuplicate={isDuplicate}
                  closeFrollFunction={onFrollClose}
                  navigateToInformationPanelFunction={
                    navigateToInfPanelFunction
                  }
                  setInforPanelHeaderFunction={onSetInfoPanelHeader}
                />
              </InformationPanelContent>
            </InformationPanelBody>
          </InformationPanel>
        )}
      </InformationPanelWrapper>
    </div>
  );
};

export default ProjectManagement;
